import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import ReadOnlyNotice from '../access/ReadOnlyNotice';
import { useAccess } from '../../services/access';
import AttentionSummary from './AttentionSummary';
import {
  T,
  Btn,
  Chip,
  SectionLabel,
  concernEntryText,
  personLabel,
} from '../project-register/shared.jsx';
import {
  answerClarification,
  approveProposal,
  listClarifications,
  listPendingCommitments,
  listPendingConcerns,
  listProposals,
  rejectProposal,
  reviewCommitment,
  reviewConcern,
} from '../../services/meetingApprovalsApi';
import {
  executeMeetingJiraSync,
  executeMeetingTaskCreation,
  getJiraProjectOptionsForMeetingTasks,
} from '../../services/workflowApi';

/* ============================================================================
   Meeting Approval Gate — the meeting-scoped "Approvals" tab.

   Four review queues over what one meeting's pipeline run produced:
   commitments and concerns (which already committed live -- see
   stage_five/service.py._prepare -- this is a review overlay, not a gate
   that blocks them from existing), work items (follow-up proposals the
   tracker drafted), and clarifications (questions the pipeline could not
   answer itself).

   Ported from a supplied reference design. Field mismatches between that
   design's spec and what the live documents actually carry are resolved in
   the adapters below (toCommitment/toConcern/toWorkItem) rather than by
   inventing backend fields that don't exist yet:
     - commitments/concerns have no `resolution_mode` field -- derived from
       supersedes_commit_id / matches_*_id.
     - commitments' ambiguity never carries candidate_options today, so the
       blocking branch always falls back to plain Accept/Discard.
     - concerns' `criticality` isn't a flat live field -- rendered only when
       present, never fabricated.
     - work items are FollowUpProposal only (no DIRECT_PUSH source exists
       yet) -- no editable before/after value, just an optional note.
     - the reference's per-audience Report tab has no backend behind it and
       is dropped for this pass.
   ========================================================================== */

/* ------------------------------------------------------------ helpers --- */

const humanise = (s) => (s ? String(s).replace(/_/g, ' ').toLowerCase() : '—');

/** Raw entity ids (hex/ObjectId strings) read as noise, not information --
    show a short prefix with the full id on hover rather than a bare hex
    blob taking up primary UI space. */
const shortRef = (id) => {
  if (!id) return null;
  const s = String(id);
  return s.length > 10 ? `${s.slice(0, 6)}…` : s;
};

// Mirrors MEETING_ACTION_TYPES_SUPPORTED_FOR_JIRA_TASK_CREATION in
// notificationRouter.py -- keep these two lists in sync. Checking this
// client-side, before a request is ever sent, is what actually prevents
// "Could not create 1 of 1: Action type 'COMMUNICATION' is not supported"
// -- the backend rejecting it after the fact just reports a failure that
// already happened; this stops the click from being possible.
export const JIRA_TASK_CREATION_SUPPORTED_TYPES = new Set([
  'TASK',
  'INVESTIGATION',
  'COMMUNICATION',
  'BLOCKER_RESOLUTION',
  'RISK_MITIGATION',
  'DECISION_FOLLOW_UP',
  'DOCUMENTATION',
  'JIRA_TASK_CREATION',
]);

/** Whether this action item's type could ever become a Jira task -- the
 * one ineligibility reason a project pick can't fix (JIRA_UPDATE belongs to
 * a different flow entirely; OTHER is too ambiguous to safely act on). */
export function isJiraTaskCreationType(item) {
  return JIRA_TASK_CREATION_SUPPORTED_TYPES.has(item.action_type);
}

/** Whether this item has no linked issue to infer a project from -- fixable
 * by picking one manually, unlike an unsupported action_type. */
export function needsManualJiraProject(item) {
  return !item.linked_entities?.issue_keys?.length;
}

/** Why this action item can't become a Jira task right now, or null if it
 * can. `hasChosenProject` says whether a manual project pick has already
 * resolved the "no linked issue" case -- pass the current override value so
 * the reason (and the checkbox it gates) clears the moment one is chosen.
 *
 * Two independent reasons the backend would otherwise reject it after the
 * fact: an action_type outside the supported set, or no linked Jira issue
 * to infer a project key from (_infer_project_key_from_issue_keys has
 * nothing to work with otherwise). Checked here so the checkbox itself
 * reflects reality instead of the user finding out via a failed toast.
 */
export function jiraTaskIneligibleReason(item, hasChosenProject = false) {
  if (!isJiraTaskCreationType(item)) {
    return `Can't create a Jira task for type ${humanise(item.action_type)}`;
  }
  if (needsManualJiraProject(item) && !hasChosenProject) {
    return 'No linked issue to infer a project from -- pick one below.';
  }
  return null;
}

function confTone(c) {
  if (c == null) return T.muted;
  if (c >= 0.85) return T.ok;
  if (c >= 0.6) return T.textSec;
  return T.warn;
}
function subtypeTone(s) {
  if (s === 'BLOCKER') return { fg: T.danger, bg: T.dangerBg };
  if (s === 'RISK') return { fg: T.warn, bg: T.warnBg };
  return { fg: T.info, bg: T.infoBg };
}

/* ------------------------------------------------------------ adapters --- */

const toCommitment = (doc) => {
  const evidence = doc.evidence || {};
  const ambiguity = doc.ambiguity || {};
  return {
    id: doc.entity_id,
    attention: doc.attention,
    resolution: doc.supersedes_commit_id
      ? { resolution_mode: 'UPDATE', target_ref: doc.supersedes_commit_id }
      : { resolution_mode: 'CREATE' },
    provenance: {
      quote: evidence.quote,
      speaker_person_id: evidence.speaker_person_id,
      at: evidence.seg_id,
      confidence: doc.attention?.confidence ?? doc.capture_confidence ?? null,
    },
    normalised_text: doc.normalised_text,
    committer_person_id: doc.committer_person_id,
    deadline_date: doc.deadline_date,
    deadline_type: doc.deadline_type,
    firmness: doc.firmness,
    condition_text: doc.condition_text,
    linked_entity_refs: doc.linked_entity_refs || [],
    ambiguity: {
      blocking: !!ambiguity.blocking,
      question_text: ambiguity.question_text,
      flags: ambiguity.flags || [],
    },
  };
};

const toConcern = (doc) => {
  const evidence = doc.evidence || {};
  const targetRef = doc.matches_prior_concern_id || doc.matches_hypothesis_id;
  const criticality = doc.risk_score?.scores?.crit ?? doc.criticality ?? null;
  return {
    id: doc.entity_id,
    attention: doc.attention,
    resolution: targetRef
      ? { resolution_mode: 'UPDATE', target_ref: targetRef }
      : { resolution_mode: 'CREATE' },
    subtype: doc.subtype,
    isHypothesis: (doc.lifecycle_stage || doc.lifecycle_state) === 'HYPOTHESIS',
    provenance: {
      quote: evidence.quote,
      speaker_person_id: evidence.speaker_person_id,
      at: evidence.seg_id,
      confidence: doc.attention?.confidence ?? doc.capture_confidence ?? null,
    },
    description: doc.description,
    linked_deliverable_id: doc.linked_deliverable_id,
    responsible_person_id: doc.responsible_person_id,
    criticality,
    combined_score:
      doc.risk_score?.combined_score ?? doc.combined_score ?? null,
    mitigation_actions: (doc.mitigation_actions || []).map(concernEntryText),
    discussions: (doc.discussions || []).map(concernEntryText),
  };
};

const toWorkItem = (p) => ({
  id: p.proposal_id,
  execution_class: 'HUMAN_FOLLOWUP',
  action_type: p.kind,
  target: p.to_person_id,
  channel: p.channel,
  payload: { text: p.message },
  quote: p.evidence?.quote,
  activity_reference: p.evidence?.activity_reference,
  reason_code: p.reason_code,
});

/* ------------------------------------------------------------ primitives --- */
// Chip and Btn are the shared project-register primitives (imported above),
// not local reimplementations -- this file used to carry its own copies with
// a sky-blue accent and different sizing, ported wholesale from a separate
// reference design. That made every button, chip, and the active-tab state
// in this screen read as a different app from the purple (#5652CF) accent
// used everywhere else. Using the shared ones keeps this screen visually
// identical to the rest of the app instead of a one-off skin.

function Quote({ text, speaker, at }) {
  if (!text) return null;
  return (
    <>
      <p
        className="text-[12px] italic leading-relaxed"
        style={{ color: T.textSec }}
      >
        &ldquo;{text}&rdquo;
      </p>
      {(speaker || at) && (
        <p className="mt-1 text-[10px]" style={{ color: T.muted }}>
          {[speaker, at].filter(Boolean).join(' · ')}
        </p>
      )}
    </>
  );
}

function Facts({ items }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {items.filter(Boolean).map(([k, v, tone], i) => (
        <span key={i} className="text-[11px]" style={{ color: T.muted }}>
          {k} <span style={{ color: tone || T.text }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

function ItemShell({ children, decided }) {
  return (
    <div
      className="rounded-xl px-3.5 py-3"
      style={{
        background: T.bg1,
        border: `1px solid ${T.br}`,
        opacity: decided ? 0.45 : 1,
        transition: 'opacity 140ms ease',
      }}
    >
      {children}
    </div>
  );
}

function TopRow({ left, needsDecision, confidence }) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5">
      {left}
      <span className="ml-auto flex items-center gap-2">
        {needsDecision && (
          <Chip fg={T.decision} bg={T.decisionBg}>
            Needs decision
          </Chip>
        )}
        {confidence != null && (
          <span className="text-[11px]" style={{ color: confTone(confidence) }}>
            {confidence.toFixed(2)}
          </span>
        )}
      </span>
    </div>
  );
}

function DecidedRow({ state, onUndo, acceptedLabel = 'Accepted' }) {
  return (
    <div
      className="mt-2.5 flex items-center gap-2 border-t pt-2"
      style={{ borderColor: T.br }}
    >
      <span
        className="text-[11px]"
        style={{ color: state === 'REJECTED' ? T.muted : T.ok }}
      >
        {state === 'REJECTED' ? 'Discarded' : acceptedLabel}
      </span>
      {onUndo && (
        <button
          onClick={onUndo}
          className="ml-auto text-[11px] focus:outline-none"
          style={{ color: T.muted }}
        >
          Undo
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- items --- */

function CommitmentItem({ c, state, busy, decide }) {
  const blocking = c.ambiguity?.blocking && !state;
  return (
    <ItemShell decided={!!state}>
      <TopRow
        needsDecision={blocking}
        confidence={c.provenance.confidence}
        left={
          <>
            {c.resolution.resolution_mode === 'UPDATE' ? (
              <Chip fg={T.info} bg={T.infoBg}>
                <span title={c.resolution.target_ref}>
                  updates {shortRef(c.resolution.target_ref)}
                </span>
              </Chip>
            ) : (
              <Chip fg={T.muted} bordered>
                new
              </Chip>
            )}
            {c.firmness === 'CONDITIONAL' && (
              <Chip fg={T.warn} bg={T.warnBg}>
                conditional
              </Chip>
            )}
          </>
        }
      />

      <Quote
        text={c.provenance.quote}
        speaker={personLabel(c.provenance.speaker_person_id)}
        at={c.provenance.at}
      />
      <AttentionSummary attention={c.attention} />

      <Facts
        items={[
          ['', c.normalised_text],
          [
            '·',
            personLabel(c.committer_person_id) ?? 'no owner',
            c.committer_person_id ? T.text : T.danger,
          ],
          c.deadline_date && [
            '·',
            `${new Date(c.deadline_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${humanise(c.deadline_type)}`,
          ],
          c.linked_entity_refs?.[0]?.ref && [
            '·',
            shortRef(c.linked_entity_refs[0].ref),
          ],
          c.condition_text && ['if', c.condition_text, T.warn],
        ]}
      />

      {blocking && c.ambiguity.question_text && (
        <p className="mt-2.5 text-[11px]" style={{ color: T.textSec }}>
          {c.ambiguity.question_text}
        </p>
      )}

      {state ? (
        <DecidedRow state={state} onUndo={null} />
      ) : !decide ? null : (
        <div className="mt-2.5 flex gap-1.5">
          <Btn
            variant="primary"
            disabled={busy}
            onClick={() => decide(c.id, 'ACCEPTED')}
          >
            Accept
          </Btn>
          <span className="ml-auto">
            <Btn
              variant="ghost"
              disabled={busy}
              onClick={() => decide(c.id, 'REJECTED')}
            >
              Discard
            </Btn>
          </span>
        </div>
      )}
    </ItemShell>
  );
}

function ConcernItem({ n, state, busy, decide }) {
  const st = subtypeTone(n.subtype);
  return (
    <ItemShell decided={!!state}>
      <TopRow
        confidence={n.provenance.confidence}
        left={
          <>
            <Chip fg={st.fg} bg={st.bg}>
              {humanise(n.subtype)}
            </Chip>
            {n.resolution.resolution_mode === 'UPDATE' ? (
              <Chip fg={T.info} bg={T.infoBg}>
                <span title={n.resolution.target_ref}>
                  updates {shortRef(n.resolution.target_ref)}
                </span>
              </Chip>
            ) : n.isHypothesis ? (
              <Chip fg={T.decision} bg={T.decisionBg}>
                hypothesis
              </Chip>
            ) : (
              <Chip fg={T.muted} bordered>
                new
              </Chip>
            )}
          </>
        }
      />

      <Quote
        text={n.provenance.quote}
        speaker={personLabel(n.provenance.speaker_person_id)}
        at={n.provenance.at}
      />
      <AttentionSummary attention={n.attention} />
      <p className="mt-2 text-[12px]" style={{ color: T.text }}>
        {n.description}
      </p>

      <Facts
        items={[
          n.linked_deliverable_id && [
            'affects',
            shortRef(n.linked_deliverable_id),
          ],
          n.responsible_person_id && [
            '·',
            personLabel(n.responsible_person_id),
          ],
          n.criticality && ['·', humanise(n.criticality)],
          // combined_score is a computed risk score (impact x likelihood),
          // not a model confidence -- labelled accordingly rather than as
          // a bare, ambiguous "score".
          n.combined_score != null && ['· risk score', n.combined_score],
        ]}
      />

      {(n.mitigation_actions.length > 0 || n.discussions.length > 0) && (
        <div
          className="mt-2.5 space-y-2 border-t pt-2.5"
          style={{ borderColor: T.br }}
        >
          {n.mitigation_actions.length > 0 && (
            <div>
              <p className="mb-1 text-[10px]" style={{ color: T.muted }}>
                Mitigation actions
              </p>
              {n.mitigation_actions.map((text, i) => (
                <p
                  key={i}
                  className="py-0.5 text-[12px]"
                  style={{ color: T.textSec }}
                >
                  · {text}
                </p>
              ))}
            </div>
          )}
          {n.discussions.length > 0 && (
            <div>
              <p className="mb-1 text-[10px]" style={{ color: T.muted }}>
                Discussion
              </p>
              {n.discussions.map((text, i) => (
                <p
                  key={i}
                  className="py-0.5 text-[12px]"
                  style={{ color: T.textSec }}
                >
                  · {text}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {state ? (
        <DecidedRow state={state} onUndo={null} />
      ) : !decide ? null : (
        <div className="mt-2.5 flex gap-1.5">
          <Btn
            variant="primary"
            disabled={busy}
            onClick={() => decide(n.id, 'ACCEPTED')}
          >
            {n.resolution.resolution_mode === 'CREATE' ? 'Admit' : 'Accept'}
          </Btn>
          <span className="ml-auto">
            <Btn
              variant="ghost"
              disabled={busy}
              onClick={() => decide(n.id, 'REJECTED')}
            >
              {n.resolution.resolution_mode === 'CREATE'
                ? 'Suppress'
                : 'Discard'}
            </Btn>
          </span>
        </div>
      )}
    </ItemShell>
  );
}

function WorkItem({ w, state, busy, decide }) {
  const [note, setNote] = useState('');
  return (
    <ItemShell decided={!!state}>
      <TopRow
        left={
          <>
            <span className="text-[11px]" style={{ color: T.text }}>
              {personLabel(w.target)}
            </span>
            <Chip fg={T.muted} bordered>
              {humanise(w.action_type)}
            </Chip>
            <Chip fg={T.muted} bordered>
              {w.channel}
            </Chip>
          </>
        }
      />

      <div className="mb-2 rounded-lg px-3 py-2" style={{ background: T.bg2 }}>
        <p className="text-[12px]" style={{ color: T.textSec }}>
          {w.payload.text}
        </p>
      </div>

      <Quote text={w.quote} />
      {w.activity_reference && (
        <p className="mt-1 text-[10px]" style={{ color: T.muted }}>
          Observed: {w.activity_reference}
        </p>
      )}
      {w.reason_code && (
        <p className="mt-1 font-mono text-[10px]" style={{ color: T.muted }}>
          {w.reason_code}
        </p>
      )}

      {state ? (
        <DecidedRow state={state} onUndo={null} acceptedLabel="Approved" />
      ) : !decide ? null : (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="min-w-[10rem] flex-1 rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus-visible:ring-1"
            style={{
              background: T.bg2,
              color: T.text,
              border: `1px solid ${T.br}`,
            }}
          />
          <Btn
            variant="primary"
            disabled={busy}
            onClick={() => decide(w.id, 'ACCEPTED', note)}
          >
            Approve &amp; send
          </Btn>
          <Btn
            variant="ghost"
            disabled={busy}
            onClick={() => decide(w.id, 'REJECTED', note)}
          >
            Skip
          </Btn>
        </div>
      )}
    </ItemShell>
  );
}

/** Work items = Jira field-update proposals + extracted action items from
    the meeting's llm_summary -- not the tracker's follow-up-message queue
    (that's a separate, narrower thing; see toWorkItem/WorkItem above, used
    only when no llmSummary is available, e.g. the embedded ProcessedMeetingContent
    caller which already has its own dedicated Jira/Actions tabs). */
function JiraUpdateItem({ update, checked, onToggle }) {
  return (
    <label
      className="flex cursor-pointer items-start gap-2.5 rounded-xl px-3.5 py-3"
      style={{ background: T.bg1, border: `1px solid ${T.br}` }}
    >
      {onToggle && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select Jira update for ${update.task_issue_key || update.issue_key}`}
          className="mt-0.5"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px]" style={{ color: T.text }}>
            {update.task_issue_key || update.issue_key}
          </span>
          <Chip fg={T.muted} bordered>
            {humanise(update.field_to_update)}
          </Chip>
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[12px]">
          <span style={{ color: T.muted, textDecoration: 'line-through' }}>
            {String(update.current_value ?? '—')}
          </span>
          <span style={{ color: T.muted }}>→</span>
          <span style={{ color: T.text }}>
            {String(update.suggested_value ?? '—')}
          </span>
        </div>
      </div>
    </label>
  );
}

function ActionItemRow({
  item,
  index,
  checked,
  onToggle,
  projectOptions,
  selectedProjectKey,
  onProjectChange,
}) {
  const canPickProject =
    isJiraTaskCreationType(item) && needsManualJiraProject(item);
  const ineligibleReason = jiraTaskIneligibleReason(item, !!selectedProjectKey);
  const checkboxId = `action-item-${index}`;

  return (
    <div
      className="flex items-start gap-2.5 rounded-xl px-3.5 py-3"
      style={{
        background: T.bg1,
        border: `1px solid ${T.br}`,
        opacity: ineligibleReason ? 0.7 : 1,
      }}
    >
      {onToggle && (
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          onChange={onToggle}
          disabled={!!ineligibleReason}
          aria-label={`Select action item: ${item.description}`}
          className={`mt-0.5 ${ineligibleReason ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
      )}
      <div className="min-w-0 flex-1">
        {/* Only the checkbox + description toggle selection -- the project
            picker below is a plain sibling, not nested inside this label,
            so clicking it can never also fire the checkbox's native
            click-toggles-labelled-control behavior. */}
        <label
          htmlFor={checkboxId}
          className={ineligibleReason ? 'cursor-not-allowed' : 'cursor-pointer'}
        >
          <p className="text-[12px]" style={{ color: T.text }}>
            {item.description}
          </p>
        </label>
        <Facts
          items={[
            // No owner_email is a real, expected state now (the pipeline
            // nulls it rather than inventing one when the person can't be
            // matched) -- say so explicitly rather than just omitting the
            // fact, so it reads as "will be created unassigned" and not as
            // missing data.
            [
              '·',
              item.owner_email ||
                item.owner_role_or_team ||
                'Unassigned (owner not resolved)',
              item.owner_email || item.owner_role_or_team ? T.text : T.muted,
            ],
            item.due_description && ['due', item.due_description, T.warn],
          ]}
        />
        {ineligibleReason && (
          <p className="mt-1.5 text-[11px]" style={{ color: T.warn }}>
            {ineligibleReason}
          </p>
        )}
        {canPickProject && onToggle && (
          <select
            value={selectedProjectKey || ''}
            onChange={(e) => onProjectChange(e.target.value || null)}
            className="mt-2 rounded-lg px-2 py-1 text-[11px] focus:outline-none focus-visible:ring-1"
            style={{
              background: T.bg2,
              color: T.text,
              border: `1px solid ${T.br}`,
            }}
          >
            <option value="">Pick a Jira project…</option>
            {projectOptions.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name ? `${p.key} — ${p.name}` : p.key}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

function ClarificationItem({ q, state, busy, decide }) {
  const [answering, setAnswering] = useState(false);
  const [resolution, setResolution] = useState('');
  const [personId, setPersonId] = useState('');
  const [entityRef, setEntityRef] = useState('');
  const blocking = (q.blocked_entity_refs?.length || 0) > 0 && !state;
  const canSubmit = resolution.trim() && personId.trim() && !busy;

  return (
    <ItemShell decided={!!state}>
      <TopRow
        needsDecision={blocking}
        left={
          <Chip fg={T.muted} bordered>
            {humanise(q.ambiguity_type)}
          </Chip>
        }
      />

      <p className="text-[12px]" style={{ color: T.text }}>
        {q.ambiguous_text}
      </p>
      <div className="mt-2">
        <Quote text={q.surrounding_context} />
      </div>

      {q.candidate_meanings?.length > 0 && (
        <p className="mt-2 text-[11px]" style={{ color: T.muted }}>
          Candidates:{' '}
          {q.candidate_meanings
            .map(
              (c) =>
                `${c.meaning ?? c.ref ?? c.label} (${(c.score ?? 0).toFixed(2)})`,
            )
            .join(', ')}
        </p>
      )}
      {q.blocked_entity_refs?.length > 0 && (
        <p
          className="mt-2 text-[11px]"
          style={{ color: T.muted }}
          title={q.blocked_entity_refs.join(', ')}
        >
          Blocks {q.blocked_entity_refs.length} item
          {q.blocked_entity_refs.length > 1 ? 's' : ''}
        </p>
      )}

      {state ? (
        <DecidedRow state={state} onUndo={null} acceptedLabel="Answered" />
      ) : !decide ? null : answering ? (
        <div className="mt-2.5 grid gap-1.5 sm:grid-cols-3">
          <input
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="What does it mean? (required)"
            className="rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus-visible:ring-1 sm:col-span-2"
            style={{
              background: T.bg2,
              color: T.text,
              border: `1px solid ${T.br}`,
            }}
          />
          <input
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            placeholder="Resolved by (required)"
            className="rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus-visible:ring-1"
            style={{
              background: T.bg2,
              color: T.text,
              border: `1px solid ${T.br}`,
            }}
          />
          <input
            value={entityRef}
            onChange={(e) => setEntityRef(e.target.value)}
            placeholder="Entity ref (optional)"
            className="rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus-visible:ring-1 sm:col-span-2"
            style={{
              background: T.bg2,
              color: T.text,
              border: `1px solid ${T.br}`,
            }}
          />
          <div className="flex gap-1.5">
            <Btn
              variant="primary"
              disabled={!canSubmit}
              onClick={() =>
                decide(q.id, {
                  resolution: resolution.trim(),
                  resolvedByPersonId: personId.trim(),
                  resolvedEntityRef: entityRef.trim(),
                })
              }
            >
              Submit answer
            </Btn>
            <Btn variant="ghost" onClick={() => setAnswering(false)}>
              Cancel
            </Btn>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <Btn variant="primary" onClick={() => setAnswering(true)}>
            Answer here
          </Btn>
          <Btn onClick={() => setAnswering(true)}>
            Ask {personLabel(q.ask_person_id) || 'the team'}
          </Btn>
        </div>
      )}
    </ItemShell>
  );
}

/* --------------------------------------------------------------- summary --- */

function SummaryView({
  commitments,
  concerns,
  workItems,
  clarifications,
  decisions,
  llmSummary,
  jiraUpdates,
  actionItems,
}) {
  const blocked =
    commitments.filter((c) => c.ambiguity?.blocking).length +
    clarifications.filter((q) => (q.blocked_entity_refs?.length || 0) > 0)
      .length;
  const pending = (list) => list.filter((x) => !decisions[x.id]).length;
  const decidedCount = (list) => list.filter((x) => decisions[x.id]).length;

  // Work items has two disjoint sources depending on whether this meeting
  // has an llm_summary: the tracker's follow-up-proposal queue (workItems,
  // which does go through the shared decisions[]/Accept-Discard flow), or
  // Jira field-update proposals + extracted action items straight from
  // llm_summary (which use a separate checkbox-and-sync flow that never
  // touches decisions[]). The tab badge (pendingCounts.WORK in the
  // container) already forks on this -- this view has to fork the same way,
  // or its "Work items" numbers read 0 the moment a meeting has an
  // llm_summary, which is most of them.
  const workItemsTotal = llmSummary
    ? jiraUpdates.length + actionItems.length
    : workItems.length;
  const workItemsPending = llmSummary ? workItemsTotal : pending(workItems);
  // No decisions[] entries ever exist for the llm_summary path, so a
  // "reviewed" count there would be invented from nothing -- left out
  // rather than always reading 0.
  const workItemsReviewedLabel = llmSummary
    ? null
    : `${decidedCount(workItems)} reviewed`;

  return (
    <>
      {/* One shared border/bg for the whole stat block, matching the
          Status/Owner/... grid every other detail panel in the app uses
          (GanttDetailPanel, Commitment/Decision/Risk detail panels) instead
          of four individually-boxed tiles with their own padding and no
          shared label typography. */}
      <div className="shadow-xs mb-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-3.5 md:grid-cols-4">
        {[
          [
            'Commitments',
            pending(commitments),
            T.text,
            `${decidedCount(commitments)} reviewed`,
          ],
          [
            'Concerns',
            pending(concerns),
            T.text,
            `${decidedCount(concerns)} reviewed`,
          ],
          ['Work items', workItemsPending, T.text, workItemsReviewedLabel],
          ['Needs decision', blocked, T.decision, 'ambiguity unresolved'],
        ].map(([label, n, tone, sub]) => (
          <div key={label} className="min-w-0">
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              {label}
            </div>
            <div
              className="mt-0.5 text-[22px] font-semibold leading-none"
              style={{ color: tone }}
            >
              {n}
            </div>
            {sub && (
              <div className="mt-1.5 truncate text-[10px] text-zinc-500">
                {sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <SectionLabel>What changed</SectionLabel>
      <div className="space-y-1.5">
        {[
          ['Commitments', commitments.length, pending(commitments)],
          ['Concerns', concerns.length, pending(concerns)],
          ['Work items', workItemsTotal, workItemsPending],
          ['Clarifications', clarifications.length, pending(clarifications)],
        ].map(([k, total, left]) => (
          <div
            key={k}
            className="flex items-center gap-3 rounded-lg border border-zinc-700/60 bg-zinc-800/50 px-3.5 py-2.5"
          >
            <span className="w-[104px] shrink-0 text-[12px] font-semibold text-zinc-100">
              {k}
            </span>
            <span className="text-[12px] text-zinc-300">
              {total} from this meeting
            </span>
            {left > 0 ? (
              <span className="ml-auto">
                <Chip fg={T.decision} bg={T.decisionBg}>
                  {left} pending
                </Chip>
              </span>
            ) : (
              <span className="ml-auto text-[11px]" style={{ color: T.muted }}>
                0 pending
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------- data hook --- */

/**
 * Owns all four queues for one meeting. Lifted out so the tab strip in
 * ProcessedMeetingContent.jsx can show a combined pending count without a
 * second fetch.
 */
export const useMeetingApprovals = (accessToken, meetingId = null) => {
  const [commitments, setCommitments] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [commitmentsRes, concernsRes, proposalsRes, clarificationsRes] =
        await Promise.all([
          meetingId
            ? listPendingCommitments(accessToken, meetingId)
            : Promise.resolve({ commitments: [] }),
          meetingId
            ? listPendingConcerns(accessToken, meetingId)
            : Promise.resolve({ concerns: [] }),
          listProposals(accessToken, { sourceMeetingId: meetingId }),
          listClarifications(accessToken, { sourceMeetingId: meetingId }),
        ]);
      setCommitments((commitmentsRes?.commitments || []).map(toCommitment));
      setConcerns((concernsRes?.concerns || []).map(toConcern));
      setWorkItems((proposalsRes?.proposals || []).map(toWorkItem));
      setClarifications(clarificationsRes?.clarifications || []);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, meetingId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    commitments,
    concerns,
    workItems,
    clarifications,
    setCommitments,
    setConcerns,
    setWorkItems,
    setClarifications,
    loading,
    error,
    setError,
    reload,
    pendingCount:
      commitments.length +
      concerns.length +
      workItems.length +
      clarifications.length,
  };
};

/* ------------------------------------------------------------- container --- */

const TABS = [
  { key: 'SUMMARY', label: 'Summary' },
  { key: 'COMMITMENTS', label: 'Commitments' },
  { key: 'CONCERNS', label: 'Concerns' },
  { key: 'WORK', label: 'Work items' },
  { key: 'CLARIFY', label: 'Clarifications', urgent: true },
];

/**
 * `meeting`: { title, occurred_at, duration_min, attendees: string[] } --
 * real values come from the calendar/manual event doc, not from the
 * (unrelated) llm_summary transcript-summary pipeline. Only rendered when
 * `standalone` is true -- MeetingSummaryPage is now this component's own
 * full page, but ProcessedMeetingContent.jsx still embeds this as one tab
 * among several elsewhere (Ui.js inline preview, MeetingEventDetailModal),
 * where a second page-level header would be redundant.
 */
const MeetingApprovalGate = ({
  accessToken,
  meetingId = null,
  queues,
  meeting = null,
  standalone = false,
  llmSummary = null,
  isManual = false,
}) => {
  const local = useMeetingApprovals(accessToken, meetingId);
  const state = queues || local;
  const {
    commitments,
    concerns,
    workItems,
    clarifications,
    loading,
    error,
    reload,
  } = state;

  const [tab, setTab] = useState('SUMMARY');
  // Deciding needs meeting:manage and Jira writes need jira:manage; without
  // them the same items show with no action rows.
  const { can } = useAccess();
  const canManage = can('meeting:manage');
  const canJira = can('jira:manage');
  const [decisions, setDecisions] = useState({});
  const [busyId, setBusyId] = useState(null);

  const decide = (id, mark) => setDecisions((p) => ({ ...p, [id]: mark }));

  const decideCommitment = async (id, mark) => {
    setBusyId(id);
    try {
      await reviewCommitment(accessToken, id, mark);
      decide(id, mark);
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setBusyId(null);
    }
  };

  const decideConcern = async (id, mark) => {
    setBusyId(id);
    try {
      await reviewConcern(accessToken, id, mark);
      decide(id, mark);
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setBusyId(null);
    }
  };

  const decideWorkItem = async (id, mark, note) => {
    setBusyId(id);
    try {
      if (mark === 'ACCEPTED')
        await approveProposal(accessToken, id, note || null);
      else await rejectProposal(accessToken, id, note || null);
      decide(id, mark);
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setBusyId(null);
    }
  };

  const decideClarification = async (id, answer) => {
    setBusyId(id);
    try {
      await answerClarification(accessToken, id, answer);
      decide(id, 'ACCEPTED');
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setBusyId(null);
    }
  };

  // Work items, when llmSummary is available: Jira field-update proposals +
  // extracted action items -- real entity/action work, not the tracker's
  // narrower follow-up-message queue.
  const jiraUpdates = useMemo(() => {
    const tasks = llmSummary?.task_wise_summary || [];
    const updates = [];
    tasks.forEach((task) => {
      (task.proposed_jira_issue_updates || []).forEach((update) => {
        updates.push({ ...update, task_issue_key: task.issue_key });
      });
    });
    return updates;
  }, [llmSummary]);
  const actionItems = useMemo(
    () => llmSummary?.extracted_action_items || [],
    [llmSummary],
  );
  const [checkedJiraIds, setCheckedJiraIds] = useState(new Set());
  const [checkedActionIds, setCheckedActionIds] = useState(new Set());
  const [workBusy, setWorkBusy] = useState(false);
  // Manual project choice for an action item with no linked issue to infer
  // one from, keyed by index into `actionItems`. Fetched lazily -- only
  // when at least one item could actually use it -- since most meetings
  // won't need this at all.
  const [jiraProjectOptions, setJiraProjectOptions] = useState([]);
  const [manualProjectByIndex, setManualProjectByIndex] = useState({});

  useEffect(() => {
    // The picker only shows to people who may write to Jira.
    if (!canJira || !accessToken || !actionItems.length) return undefined;
    const needsPicker = actionItems.some(
      (item) => isJiraTaskCreationType(item) && needsManualJiraProject(item),
    );
    if (!needsPicker) return undefined;
    let cancelled = false;
    getJiraProjectOptionsForMeetingTasks(accessToken)
      .then((res) => {
        if (!cancelled) setJiraProjectOptions(res?.projects || []);
      })
      .catch(() => {
        // Best-effort: an empty picker is a smaller failure than the
        // request itself blocking the rest of this tab from rendering.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, actionItems, canJira]);

  const toggleJiraChecked = (idx) =>
    setCheckedJiraIds((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  const toggleActionChecked = (idx) =>
    setCheckedActionIds((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  // The backend never fails the whole request for a per-item problem -- it
  // always returns 200 with one result per id, each independently
  // success/failed (notificationRouter.py: _sync_meeting_jira_updates /
  // _sync_meeting_action_item_tasks). Reading only "did the HTTP call
  // succeed" and ignoring that array meant a request where every single
  // item failed still read as a clean success: checkboxes cleared, no
  // error, nothing to tell the difference from a real sync. That's exactly
  // the state that gets someone re-clicking "Create" on the same items,
  // filing the same Jira task twice.
  const summarizeJiraOutcome = (
    result,
    selectedCount,
    { successVerb, failVerb },
  ) => {
    const results = result?.results || [];
    const successCount = result?.success_count ?? 0;
    const failedCount = result?.failed_count ?? selectedCount - successCount;
    const failures = results.filter((r) => r.status !== 'success');

    if (failedCount === 0) {
      toast.success(`${successVerb} ${successCount} of ${selectedCount}.`);
      return;
    }
    if (successCount === 0) {
      const detail = failures[0]?.message;
      toast.error(
        `${failVerb} ${failedCount} of ${selectedCount}` +
          (detail ? `: ${detail}` : '.') +
          (failedCount > 1 ? ' (see console for the rest)' : ''),
        { duration: 6000 },
      );
    } else {
      toast(
        `${successVerb} ${successCount} of ${selectedCount}. ` +
          `${failedCount} failed and stayed checked below.`,
        { icon: '⚠️', duration: 6000 },
      );
    }
    if (failures.length) {
      // eslint-disable-next-line no-console
      console.warn('[MeetingApprovalGate] Jira action failures:', failures);
    }
  };

  const handleSyncJira = async () => {
    const selected = jiraUpdates
      .map((u, i) => ({ update: u, i }))
      .filter(({ i }) => checkedJiraIds.has(i))
      .filter(({ update }) => Boolean(update.update_id));
    if (!selected.length) return;
    setWorkBusy(true);
    try {
      const result = await executeMeetingJiraSync(
        accessToken,
        meetingId,
        isManual,
        selected.map(({ update }) => update.update_id),
      );
      // Match by update_id (echoed back in every result row), not position
      // -- robust to the backend reordering or dropping a row.
      const outcomeById = new Map(
        (result?.results || []).map((r) => [r.update_id, r]),
      );
      setCheckedJiraIds((prev) => {
        const next = new Set(prev);
        selected.forEach(({ update, i }) => {
          if (outcomeById.get(update.update_id)?.status === 'success') {
            next.delete(i);
          }
        });
        return next;
      });
      summarizeJiraOutcome(result, selected.length, {
        successVerb: 'Synced',
        failVerb: 'Could not sync',
      });
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setWorkBusy(false);
    }
  };

  const handleCreateActions = async () => {
    // .trim() matches the backend's own _get_action_item_id, which strips
    // each field before hashing them into the id. Without it, a description
    // carrying incidental whitespace (plausible in LLM output, never
    // guaranteed clean) hashes to a different id here than there, and the
    // item comes back "not found" even though it's the exact one selected.
    const selected = actionItems
      .map((item, i) => ({
        item,
        i,
        actionId: `action:${(item.description || '').trim()}:${(item.owner_email || '').trim()}:${(item.due_description || '').trim()}:${i}`,
        manualProject: manualProjectByIndex[i] || null,
      }))
      .filter(({ i }) => checkedActionIds.has(i))
      // Defense in depth -- the checkbox is disabled for these, so this
      // only matters for a stale checked-set from before this eligibility
      // check existed. Silently drop rather than send a request known to
      // fail.
      .filter(
        ({ item, manualProject }) =>
          !jiraTaskIneligibleReason(item, !!manualProject),
      );
    if (!selected.length) return;
    setWorkBusy(true);
    try {
      const projectOverrides = Object.fromEntries(
        selected
          .filter(({ manualProject }) => manualProject)
          .map(({ actionId, manualProject }) => [actionId, manualProject]),
      );
      const result = await executeMeetingTaskCreation(
        accessToken,
        meetingId,
        isManual,
        selected.map(({ actionId }) => actionId),
        projectOverrides,
      );
      const outcomeById = new Map(
        (result?.results || []).map((r) => [r.action_id, r]),
      );
      setCheckedActionIds((prev) => {
        const next = new Set(prev);
        selected.forEach(({ actionId, i }) => {
          if (outcomeById.get(actionId)?.status === 'success') {
            next.delete(i);
          }
        });
        return next;
      });
      // Clear the project pick too, once it actually did its job -- a
      // retried failure should keep the chosen project pre-filled, but a
      // success has nothing left to remember it for.
      setManualProjectByIndex((prev) => {
        const next = { ...prev };
        selected.forEach(({ actionId, i }) => {
          if (outcomeById.get(actionId)?.status === 'success') {
            delete next[i];
          }
        });
        return next;
      });
      summarizeJiraOutcome(result, selected.length, {
        successVerb: 'Created',
        failVerb: 'Could not create',
      });
    } catch (e) {
      state.setError?.(String(e?.message || e));
    } finally {
      setWorkBusy(false);
    }
  };

  const pendingCounts = useMemo(
    () => ({
      COMMITMENTS: commitments.filter((c) => !decisions[c.id]).length,
      CONCERNS: concerns.filter((n) => !decisions[n.id]).length,
      WORK: llmSummary
        ? jiraUpdates.length + actionItems.length
        : workItems.filter((w) => !decisions[w.id]).length,
      CLARIFY: clarifications.filter((q) => !decisions[q.clarification_id])
        .length,
    }),
    [
      commitments,
      concerns,
      workItems,
      clarifications,
      decisions,
      llmSummary,
      jiraUpdates,
      actionItems,
    ],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: T.info }} />
      </div>
    );
  }

  return (
    <div style={{ color: T.text }}>
      <ReadOnlyNotice permission="meeting:manage">
        You can read these items. Only a team lead or a project manager can
        approve, reject or answer them.
      </ReadOnlyNotice>
      {standalone && meeting && (
        <div className="mb-4">
          <h1 className="text-[16px] font-medium">
            {meeting.title} ·{' '}
            {meeting.occurred_at
              ? new Date(meeting.occurred_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })
              : '—'}
          </h1>
          <p className="mt-1 text-[12px]" style={{ color: T.muted }}>
            {meeting.attendees?.length ?? 0} attendees
            {meeting.duration_min
              ? ` · ${meeting.duration_min} min`
              : ''} ·{' '}
            {commitments.length +
              concerns.length +
              workItems.length +
              clarifications.length}{' '}
            items · {Object.keys(decisions).length} reviewed
          </p>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between gap-2">
        <p className="max-w-2xl text-[12px]" style={{ color: T.muted }}>
          What this meeting produced, held for review. Approving a commitment or
          concern only clears it from this queue — it already committed to the
          register.{' '}
          {llmSummary
            ? 'Checking a Jira update or action item and syncing/creating it does write to Jira.'
            : 'Approving a work item records a decision; there is no send path yet, so nothing leaves the system.'}
        </p>
        {/* Ghost + a scoped label, not a second "Refresh": the modal header
            above this panel already has one that reloads the meeting itself
            (metadata + llm_summary). This one only reloads the four approval
            queues below, which the header's refresh doesn't touch -- both
            are real, but two identically-styled "Refresh" buttons stacked in
            the same view read as an accidental duplicate. */}
        <Btn variant="ghost" onClick={reload}>
          Refresh queue
        </Btn>
      </div>

      {error && (
        <div
          className="mb-4 rounded-lg px-3 py-2 text-[13px]"
          style={{
            color: T.danger,
            background: T.dangerBg,
            border: '1px solid rgba(248,113,113,0.3)',
          }}
        >
          {error}
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            // Active state uses the app's actual primary (T.primary,
            // #5652CF) instead of the sky-blue this screen used to be alone
            // in using -- the same accent the header buttons, tab pills
            // elsewhere, and every register panel's focus/active state use.
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors focus:outline-none focus-visible:ring-1"
            style={{
              color: tab === t.key ? T.text : T.textSec,
              background: tab === t.key ? T.primaryBg : T.bg2,
              border: `1px solid ${tab === t.key ? T.primary : T.br}`,
            }}
          >
            {t.label}
            {pendingCounts[t.key] > 0 && (
              <span
                className="rounded-full px-1.5 text-[10px] font-bold"
                style={{
                  background: t.urgent ? T.dangerBg : T.bg1,
                  color: t.urgent ? T.danger : T.muted,
                }}
              >
                {pendingCounts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'SUMMARY' && (
        <SummaryView
          commitments={commitments}
          concerns={concerns}
          workItems={workItems}
          clarifications={clarifications}
          decisions={decisions}
          llmSummary={llmSummary}
          jiraUpdates={jiraUpdates}
          actionItems={actionItems}
        />
      )}

      {tab === 'COMMITMENTS' &&
        (commitments.length === 0 ? (
          <EmptyNote>No new commitments from this meeting.</EmptyNote>
        ) : (
          <div className="space-y-2.5">
            {[...commitments]
              .sort(
                (a, b) =>
                  (b.ambiguity?.blocking ? 1 : 0) -
                  (a.ambiguity?.blocking ? 1 : 0),
              )
              .map((c) => (
                <CommitmentItem
                  key={c.id}
                  c={c}
                  state={decisions[c.id]}
                  busy={busyId === c.id}
                  decide={canManage ? decideCommitment : null}
                />
              ))}
          </div>
        ))}

      {tab === 'CONCERNS' &&
        (concerns.length === 0 ? (
          <EmptyNote>No new concerns from this meeting.</EmptyNote>
        ) : (
          <div className="space-y-2.5">
            {concerns.map((n) => (
              <ConcernItem
                key={n.id}
                n={n}
                state={decisions[n.id]}
                busy={busyId === n.id}
                decide={canManage ? decideConcern : null}
              />
            ))}
          </div>
        ))}

      {tab === 'WORK' &&
        (llmSummary ? (
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[11px]" style={{ color: T.textSec }}>
                  Jira updates · {jiraUpdates.length}
                </p>
                {canJira && checkedJiraIds.size > 0 && (
                  <Btn
                    variant="primary"
                    disabled={workBusy}
                    onClick={handleSyncJira}
                  >
                    Sync {checkedJiraIds.size} update
                    {checkedJiraIds.size > 1 ? 's' : ''}
                  </Btn>
                )}
              </div>
              {jiraUpdates.length === 0 ? (
                <EmptyNote>No Jira updates from this meeting.</EmptyNote>
              ) : (
                <div className="space-y-2">
                  {jiraUpdates.map((u, i) => (
                    <JiraUpdateItem
                      key={u.update_id || i}
                      update={u}
                      checked={checkedJiraIds.has(i)}
                      onToggle={canJira ? () => toggleJiraChecked(i) : null}
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[11px]" style={{ color: T.textSec }}>
                  Action items · {actionItems.length}
                </p>
                {canJira && checkedActionIds.size > 0 && (
                  <Btn
                    variant="primary"
                    disabled={workBusy}
                    onClick={handleCreateActions}
                  >
                    Create {checkedActionIds.size} task
                    {checkedActionIds.size > 1 ? 's' : ''}
                  </Btn>
                )}
              </div>
              {actionItems.length === 0 ? (
                <EmptyNote>No action items from this meeting.</EmptyNote>
              ) : (
                <div className="space-y-2">
                  {actionItems.map((item, i) => (
                    <ActionItemRow
                      key={i}
                      item={item}
                      index={i}
                      checked={checkedActionIds.has(i)}
                      onToggle={canJira ? () => toggleActionChecked(i) : null}
                      projectOptions={jiraProjectOptions}
                      selectedProjectKey={manualProjectByIndex[i] || null}
                      onProjectChange={(projectKey) =>
                        setManualProjectByIndex((prev) => {
                          const next = { ...prev };
                          if (projectKey) next[i] = projectKey;
                          else delete next[i];
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="mb-3 text-[11px]" style={{ color: T.muted }}>
              Nothing is sent until approved.
            </p>
            {workItems.length === 0 ? (
              <EmptyNote>Nothing awaiting approval.</EmptyNote>
            ) : (
              <div className="space-y-2.5">
                {workItems.map((w) => (
                  <WorkItem
                    key={w.id}
                    w={w}
                    state={decisions[w.id]}
                    busy={busyId === w.id}
                    decide={canManage ? decideWorkItem : null}
                  />
                ))}
              </div>
            )}
          </>
        ))}

      {tab === 'CLARIFY' &&
        (clarifications.length === 0 ? (
          <EmptyNote>No open questions.</EmptyNote>
        ) : (
          <div className="space-y-2.5">
            {[...clarifications]
              .sort(
                (a, b) =>
                  (b.blocked_entity_refs?.length || 0) -
                  (a.blocked_entity_refs?.length || 0),
              )
              .map((q) => (
                <ClarificationItem
                  key={q.clarification_id}
                  q={q}
                  state={decisions[q.clarification_id]}
                  busy={busyId === q.clarification_id}
                  decide={canManage ? decideClarification : null}
                />
              ))}
          </div>
        ))}
    </div>
  );
};

function EmptyNote({ children }) {
  return (
    <div
      className="rounded-xl py-16 text-center text-[13px]"
      style={{ background: T.bg1, border: `1px solid ${T.br}`, color: T.muted }}
    >
      {children}
    </div>
  );
}

export default MeetingApprovalGate;
